import React from 'react';

/**
 * TaskTitleWithClient — canonical renderer for the
 * "Company Name — Task Name" row format used across the CRM.
 *
 * Owner rule (2026-06-05):
 *   Any time a task appears in the format "Company Name — Task Name"
 *   (em-dash separator), the company portion MUST render in the CRM's
 *   accent colour (`text-gold`) at font-weight 600 so it visually
 *   stands out from the rest of the row.
 *
 * Theme rule (standing, indefinite):
 *   All styling MUST work in BOTH dark and light mode. We use the
 *   theme-aware Tailwind utility `text-gold` (backed by the
 *   `--gold` CSS variable in `src/index.css` — H:27 96% 44% in light
 *   mode for AA contrast on white, H:38 92% 50% in dark mode). NEVER
 *   hardcode a hex value here.
 *
 * Props:
 *   clientName  string | null   — the company / client name. When
 *                                falsy, the row renders title-only
 *                                (no leading separator).
 *   title       string          — the task title.
 *   suffix      string | null   — optional trailing text after the
 *                                title (e.g. a formatted due date).
 *                                Prepended with " — " automatically.
 *   testId      string | null   — optional data-testid for the
 *                                styled client-name span.
 *
 * Render contract — only the company name receives styling. The
 * separator, title, and any suffix are left in the default body
 * colour so the task name reads at normal weight.
 */
export default function TaskTitleWithClient({ clientName, title, suffix, testId }) {
  const hasClient = !!(clientName && String(clientName).trim());
  const hasSuffix = !!(suffix && String(suffix).trim());
  return (
    <>
      {hasClient && (
        <>
          <span
            className="text-gold font-semibold"
            data-testid={testId || undefined}
          >
            {clientName}
          </span>
          {' — '}
        </>
      )}
      {title}
      {hasSuffix && (<>{' — '}{suffix}</>)}
    </>
  );
}
